/*
    Which connections decide the SHAPE of a tidied canvas.

    A workspace carries nineteen kinds of connection and they do not mean the same thing to a
    layout. "A is the parent of B" and "A contains B" put B underneath A. "A depends on B" does
    too, in a different vocabulary. "A supports B" is evidence pointing at a claim. But "A
    competes with B" says the two are RIVALS — peers, side by side — and "A relates to B" barely
    says anything at all.

    Feeding all of them to dagre as equal ranking edges produces confident nonsense: three
    competing hypotheses drawn as a chain, as though each descended from the one above it.

    So kinds are ranked in TIERS, and the most structural tier PRESENT on the canvas decides the
    shape. Its edges are weighted far above the rest — dagre minimises total weighted edge
    length, so forty ordinary connections would have to pull together to bend one parent_of.
    That margin is load-bearing: a tree drawn by the assistant usually carries other lines too
    (a "relates" back to whoever the conversation started with, a source cited by three people),
    and given EQUAL say those extra lines flatten the tree — whoever has the most connections
    becomes the root, which is how parents once ended up below their own children.

    The lesser edges still rank, faintly. Dropping them outright was the first fix and it cost
    something real: a note joined to one component by a plain "relates" had nothing holding it
    anywhere, so it floated up to the top rank instead of sitting beside the thing it annotates.
    A small weight keeps it anchored without letting it argue about the shape.

    Peers are the exception, and are never ranked at all.

    One consequence worth knowing: dagre keeps at least one rank between the ends of any edge it
    is given, so two siblings with a connection BETWEEN them end up one below the other rather
    than side by side. Siblings with nothing joining them stay on one row. That was judged the
    better trade for anchoring — see layout.test.js, which pins both halves of it.
*/

/** How much more say the structural tier gets than everything else. */
export const RANKING_WEIGHT = 40;

export const FAINT_WEIGHT = 1;

/** Tiers, most structural first. The first tier with an edge on the canvas decides the shape. */
export const EDGE_TIERS = [
    // Containment: what a thing is PART OF. A family tree, an org chart, a C4 container.
    ['parent_of', 'contains'],
    // Systems flow: what a component is to another component.
    ['depends_on', 'flows_to', 'uses', 'on_success', 'on_failure'],
    // Derivation: evidence, citations and definitions pointing at what they bear on.
    ['supports', 'contradicts', 'context', 'cites', 'answers', 'flags', 'example_of',
        'consistent', 'inconsistent', 'about', 'defines_for', 'from_source'],
    // Generic. Ranks only when nothing more meaningful is on the canvas — but it must still
    // rank, because `relates` is the default kind and boards drawn entirely with it are common.
    ['relates'],
];

/** Human names for the tiers, so Tidy can say WHY the canvas came out the shape it did. */
export const TIER_LABELS = ['containment', 'dependency', 'evidence', 'connection'];

/**
 * Connections that are symmetric: they say two cards are peers, not that one is under the
 * other. Never ranked, in any tier — ranking a rivalry invents a hierarchy nobody drew.
 */
export const PEER_KINDS = ['alternative_to'];

/** Tiers whose shape a chronology would destroy — a dated family tree is still a tree. */
const STRUCTURAL_TIERS = [0, 1];

/** An edge's kind, defaulting the way the rest of the canvas does. */
export function edgeKind(edge) {
    return edge?.data?.kind ?? 'relates';
}

/**
 * Decide the ranking edges for one tidy pass.
 *
 * @param {Array<{source: string, target: string, data?: {kind?: string}}>} edges
 *        Edges already filtered to those with both ends loose on the canvas.
 * @returns {{tier: number, label: string|null, structural: boolean,
 *            ranked: Array<{source: string, target: string, weight: number}>}}
 *          `ranked` carries every non-peer edge; the winning tier's weigh RANKING_WEIGHT and
 *          the rest FAINT_WEIGHT, so the lesser ones anchor cards without reshaping the board.
 */
export function planLayout(edges) {
    const present = (tier) => edges.some((e) => tier.includes(edgeKind(e)));
    const tier = EDGE_TIERS.findIndex(present);

    // No tier at all — a canvas of pure rivals, or of nothing. Dagre gets no edges and lays
    // the cards out in a single rank, which is exactly what peers should look like.
    if (tier === -1) {
        return { tier: -1, label: null, structural: false, ranked: [] };
    }

    const kinds = EDGE_TIERS[tier];

    return {
        tier,
        label: TIER_LABELS[tier] ?? null,
        structural: STRUCTURAL_TIERS.includes(tier),
        ranked: edges
            .filter((e) => !PEER_KINDS.includes(edgeKind(e)))
            .map((e) => ({
                source: e.source,
                target: e.target,
                weight: kinds.includes(edgeKind(e)) ? RANKING_WEIGHT : FAINT_WEIGHT,
            })),
    };
}
